import { useState } from "react";
import { Flex, Text, Icon, Button, Tooltip, Box } from "@chakra-ui/react";

import { FaCircleXmark, FaCircleCheck } from "react-icons/fa6";

import {
  textPrimary,
  textSecondary,
  buttonGreen,
  buttonGreenText,
  greyHover,
  buttonGreenDark,
  white,
} from "../../utils/colors.js";
import { useMotionValue } from "framer-motion";
import PriceQuantityPopover from "../popovers/PriceQuantityPopover.js";
import { useTranslation } from "react-i18next";
// import TeethPopoverNew from "../popovers/TeethPopoverNew.js";
import useThemeColors from "../../hooks/useThemeColors.js";
import WarningModal from "../buttons/partials/WarningModal.js";

const ServiceItemNewMobile = ({
  service,
  saveEditedService,
  removeService,
  isEdit,
  tpStatus,
  setStatus,
  oneLeftToComplete,
  oneLeftToUncomplete,
  resetServicesStatusToUncompleted,
  currency,
}) => {
  const { t } = useTranslation();
  const { backgroundLightMode, textPrimaryMode } = useThemeColors();

  const [isExpanded, setIsExpanded] = useState(false);
  const y = useMotionValue(0);

  const [selectedTeeth, setSelectedTeeth] = useState(service.teeth || []);

  const [serviceStatus, setServiceStatus] = useState(service.status); // Add status state

  const [serviceIdToRemove, setServiceIdToRemove] = useState(null);

  const [warningModal, setWarningModal] = useState(false);
  const [warningTitle, setWarningTitle] = useState("");
  const [warningMessage, setWarningMessage] = useState("");
  const [warningButtons, setWarningButtons] = useState(false);
  const [pendingStatus, setPendingStatus] = useState("");

  const [modalType, setModalType] = useState("");

  const [tempSelectedTeeth, setTempSelectedTeeth] = useState([]);

  const isServicePriceVariations = isEdit
    ? service.service.priceVariations === true
    : service.priceVariations === true;

  const toggleAccordion = () => {
    setIsExpanded(!isExpanded);
  };

  const toggleServiceStatus = (e) => {
    e.stopPropagation();

    // Check if treatment plan status is approved
    if (tpStatus === "approved") {
      // console.log("LOOOOOOOK HERE");
      if (oneLeftToComplete) {
        setModalType("service.statusChange");
        setWarningTitle(t("warnings.completedStatusWarningTitle"));
        setWarningMessage(t("warnings.completedStatusWarningMessage"));
        setPendingStatus("completed");
        setWarningButtons(true);
        setWarningModal(true);
      } else {
        setModalType("service.statusChange");
        setWarningTitle(t("warnings.ongoingStatusWarningTitle"));
        setWarningMessage(t("warnings.ongoingStatusWarningMessage"));
        setPendingStatus("ongoing");
        setWarningButtons(true);
        setWarningModal(true);
      }
    }
    // If treatment plan status is ongoing
    else if (tpStatus === "ongoing") {
      // Check when the service is completed
      if (service.status === "completed") {
        if (oneLeftToUncomplete) {
          // Handle the scenario where uncompletion is needed
          setModalType("service.statusChange");
          setWarningTitle(t("warnings.ongoingToPlanningWarningStatusTitle"));
          setWarningMessage(
            t("warnings.ongoingToPlanningWarningStatusMessage")
          );
          setPendingStatus("planning");
          setWarningButtons(true);
          setWarningModal(true);
        } else {
          // Default behavior when toggling the service status
          setServiceStatus("uncompleted");
          saveEditedService({
            ...service,
            status: "uncompleted",
          });
        }
      }

      // Check when the service is uncompleted
      else if (service.status === "uncompleted") {
        if (oneLeftToComplete) {
          // Handle the scenario where completion is needed
          setModalType("service.statusChange");
          setWarningTitle(t("warnings.completedStatusWarningTitle"));
          setWarningMessage(t("warnings.completedStatusWarningMessage"));
          setPendingStatus("completed");
          setWarningButtons(true);
          setWarningModal(true);
        } else {
          // Default behavior when toggling the service status
          setServiceStatus("completed");
          saveEditedService({
            ...service,
            status: "completed",
          });
        }
      } else {
        setServiceStatus(
          service.status === "completed" ? "uncompleted" : "completed"
        );
        saveEditedService({
          ...service,
          status: service.status === "completed" ? "uncompleted" : "completed",
        });
      }
    }
    // If treatment plan status is anything other than "approved" or "ongoing"
    else {
      setServiceStatus(
        service.status === "completed" ? "uncompleted" : "completed"
      );
      saveEditedService({
        ...service,
        status: service.status === "completed" ? "uncompleted" : "completed",
      });
    }
  };

  // const toggleServiceStatus = (e) => {
  //   e.stopPropagation();

  //   setServiceStatus(
  //     service.status === "completed" ? "uncompleted" : "completed"
  //   );
  //   saveEditedService({
  //     ...service,
  //     status: service.status === "completed" ? "uncompleted" : "completed",
  //   });
  // };

  const confirmServiceStatusChange = (e) => {
    setWarningModal(false);
    if (pendingStatus) {
      // if modal popped on toggleServiceStatus
      setServiceStatus(
        service.status === "completed" ? "uncompleted" : "completed"
      );
      saveEditedService({
        ...service,
        status: service.status === "completed" ? "uncompleted" : "completed",
      });
    }
    setStatus(pendingStatus);
    setPendingStatus("");
  };

  const cancelModal = () => {
    setWarningModal(false);
  };

  const confirmRemoveService = (serviceId) => {
    removeService(serviceId);
    setWarningModal(false);
  };

  const isQuantityZero = service.quantity === 0 && service.teeth.length === 0;

  const handleToothClick = (toothNumber, event) => {
    event.stopPropagation(); // Stop event from bubbling up
    const updatedSelectedTeeth = selectedTeeth.includes(toothNumber)
      ? selectedTeeth.filter((t) => t !== toothNumber) // Deselect if already selected
      : [...selectedTeeth, toothNumber]; // Select if not already selected

    // Store temporary values for confirmation

    setTempSelectedTeeth(updatedSelectedTeeth);

    if (tpStatus === "approved") {
      setWarningTitle(t("warnings.editServiceStatusWarningTitle"));
      setWarningMessage(t("warnings.editServiceStatusWarningMessage"));
      setPendingStatus("planning");
      setWarningModal(true);
      setWarningButtons(true);
      setModalType("toothSelection");
    } else if (tpStatus === "ongoing") {
      setWarningTitle(t("warnings.ongoingToothSelectionStatusWarningTitle"));
      setWarningMessage(
        t("warnings.ongoingToothSelectionStatusWarningMessage")
      );
      setPendingStatus("planning");
      setWarningModal(true);
      setWarningButtons(true);
      setModalType("toothSelection");
    } else {
      setSelectedTeeth(updatedSelectedTeeth);

      saveEditedService({
        ...service,
        teeth: updatedSelectedTeeth,
        totalPrice: service.price * updatedSelectedTeeth.length,
        quantity: updatedSelectedTeeth.length,
      });
    }
  };

  const confirmTeethSelection = () => {
    // Ensure tempSelectedTeeth is used
    const updatedService = {
      ...service,
      teeth: tempSelectedTeeth,
      totalPrice: service.price * tempSelectedTeeth.length,
      quantity: tempSelectedTeeth.length,
    };

    // Save the updated service
    saveEditedService(updatedService);

    // Update the actual selected teeth state
    setSelectedTeeth(tempSelectedTeeth);

    // Clear temporary states

    setTempSelectedTeeth([]);

    // Update the status if a status change was pending
    if (pendingStatus) {
      setStatus(pendingStatus);
      setPendingStatus("");
      if (pendingStatus === "planning") {
        resetServicesStatusToUncompleted();
      }
    }

    // Close the modal
    setWarningModal(false);
  };

  const removeWithCheckService = (serviceId) => (e) => {
    e.stopPropagation();
    if (tpStatus === "approved") {
      setWarningTitle(t("warnings.removeServiceStatusWarningTitle"));
      setWarningMessage(t("warnings.removeServiceStatusWarningMessage"));
      setPendingStatus("planning");
      setWarningModal(true);
      setWarningButtons(true);

      setModalType("removingService");
      setServiceIdToRemove(serviceId); // Store the serviceId here
    } else if (tpStatus === "ongoing") {
      setWarningTitle(t("warnings.removeServiceStatusWarningTitle"));
      setWarningMessage(t("warnings.removeServiceOngoingStatusWarningMessage"));
      setPendingStatus("planning");
      setWarningModal(true);
      setWarningButtons(true);

      setModalType("removingService");
      setServiceIdToRemove(serviceId); // Store the serviceId here
    } else {
      removeService(serviceId);
    }
  };

  //WARNING MODAL  MAIN HANDLER
  const confirmModalHandler = () => {
    if (modalType === "removingService" && serviceIdToRemove) {
      confirmRemoveService(serviceIdToRemove);
      if (pendingStatus === "planning") {
        resetServicesStatusToUncompleted();
      }
    } else if (modalType === "service.statusChange") {
      confirmServiceStatusChange();
    } else if (modalType === "toothSelection") {
      // console.log("confirmTeethSelection");
      confirmTeethSelection();
    }
  };

  // console.log("selectedTeeth", selectedTeeth);
  // console.log("servicename length", service.service.name.length);
  // console.log("tpStatus", tpStatus);
  // console.log("modalType", modalType);
  // console.log("warningModal", warningModal);
  // console.log("pendingStatus", pendingStatus);
  // console.log("warningTitle", warningTitle);
  // console.log("warningMessage", warningMessage);

  // console.log("isAllServicesUncompleted", isAllServicesUncompleted);
  // console.log("isAnyServiceComplete", isAnyServiceComplete);
  // console.log("isAllServicesCompleted", isAllServicesCompleted);

  // console.log("service.status", service.status);

  // console.log("allCompleted", allCompleted);
  // console.log("allUncompleted", allUncompleted);

  // console.log("tpStatus", tpStatus);
  // console.log("pendingStatus", pendingStatus);

  // console.log("allCompleted", allCompleted);
  // console.log("allUncompleted", allUncompleted);
  // console.log("oneLeftToComplete", oneLeftToComplete);
  // console.log("oneLeftToUncomplete", oneLeftToUncomplete);

  // console.log("service.status", service.status);

  // console.log("tpStatus in ServiceItemNew", tpStatus);
  // console.log("modalType in ServiceItemNew", modalType);

  // console.log("isEdit", isEdit);

  // console.log("service", service);

  // console.log("currency", currency);

  return (
    <>
      {warningModal && (
        <WarningModal
          open={warningModal}
          setOpen={setWarningModal}
          warningTitle={warningTitle}
          warningMessage={warningMessage}
          warningButtons={warningButtons}
          statusBefore={tpStatus}
          statusAfter={pendingStatus}
          confirmHandler={confirmModalHandler}
          cancelHandler={cancelModal}
          pendingStatus={pendingStatus}
        />
      )}

      <Box mb="0.75rem" p="0 1.5rem">
        <Flex
          w="100%"
          justify="space-between"
          align="center"
          p="1rem"
          bg={white}
          borderRadius="1rem"
          direction="column"
        >
          <Flex align="center" justify="space-between" w="100%">
            <Flex align="center">
              {/* <Icon
              fontSize="0.75rem"
              as={FaGrip}
              color={textSecondary}
              opacity="0.8"
              mr="0.75rem"
            /> */}
              <Text fontWeight="600" fontSize="sm" color={textPrimaryMode}>
                <Text as="span" fontWeight="500" color={textPrimary}>
                  {service.order}.
                </Text>{" "}
                {service?.service?.name}
              </Text>
            </Flex>

            <Flex align="center" justify="flex-end" gap="0.5rem"></Flex>
          </Flex>
          <Flex w="100%" align="center" justify="space-between" m="1rem 0 0">
            <Flex align="center">
              <Tooltip
                p="0.5rem 0.5rem"
                borderRadius="1rem"
                placement="top"
                label={
                  <Flex direction="column" align="center" justify="center">
                    {tpStatus === "planning" ? (
                      <Text fontSize="xs" fontWeight="500" textAlign="center">
                        {t(
                          "treatmentPlan.tooltipServiceToggleWarning-planning"
                        )}
                      </Text>
                    ) : tpStatus === "approved" ? (
                      <Text fontSize="xs" fontWeight="500" textAlign="center">
                        {service.status === "completed"
                          ? t("treatmentPlan.clickToUncomplete")
                          : t("treatmentPlan.clickToComplete")}
                      </Text>
                    ) : tpStatus === "cancelled" ? (
                      <Text fontSize="xs" fontWeight="500" textAlign="center">
                        {t("warnings.tooltipCancelledChangePriceQuantityText")}
                      </Text>
                    ) : tpStatus === "completed" ? (
                      <Text fontSize="xs" fontWeight="500" textAlign="center">
                        {t("warnings.tooltipCompletedChangePriceQuantityText")}
                      </Text>
                    ) : (
                      <Text fontSize="xs" fontWeight="500" textAlign="center">
                        {service.status === "completed"
                          ? t("treatmentPlan.clickToUncomplete")
                          : t("treatmentPlan.clickToComplete")}
                      </Text>
                    )}
                  </Flex>
                }
                // isDisabled={tpStatus === "completed"}
              >
                <>
                  <Button
                    size="sm"
                    h={["2rem", "2.25rem"]}
                    minW="6.5rem"
                    px="1.125rem"
                    fontSize="0.8rem"
                    fontWeight="600"
                    isDisabled={
                      tpStatus === "planning" ||
                      tpStatus === "cancelled" ||
                      tpStatus === "completed"
                    }
                    bg={
                      service.status === "completed"
                        ? buttonGreen
                        : backgroundLightMode
                    }
                    color={
                      service.status === "completed"
                        ? buttonGreenText
                        : textSecondary
                    }
                    borderRadius="0.75rem"
                    onClick={toggleServiceStatus}
                    _hover={{
                      bg:
                        service.status === "completed"
                          ? buttonGreenDark
                          : greyHover,
                      color:
                        service.status === "completed"
                          ? buttonGreenText
                          : textSecondary,
                    }}
                  >
                    <Icon
                      as={
                        service.status === "completed"
                          ? FaCircleCheck
                          : FaCircleXmark
                      }
                      fontSize="0.6rem"
                      mr="0.25rem"
                    />

                    <Text as="span" fontSize="xs" fontWeight="500">
                      {service.status === "completed"
                        ? t("status.serviceCompleted")
                        : t("status.serviceUncompleted")}
                    </Text>
                  </Button>
                </>
              </Tooltip>
            </Flex>

            <Flex align="center" justify="flex-end" gap="0.5rem">
              <PriceQuantityPopover
                service={service}
                saveEditedService={saveEditedService}
                isQuantityZero={isQuantityZero}
                isExpanded={isExpanded}
                isEdit={isEdit}
                isServicePriceVariations={isServicePriceVariations}
                tpStatus={tpStatus}
                setStatus={setStatus}
                resetServicesStatusToUncompleted={
                  resetServicesStatusToUncompleted
                }
                currency={currency}
              />{" "}
            </Flex>
          </Flex>
        </Flex>

        {/* {isServicePriceVariations && (
          <TeethPopoverNew
            handleToothClick={handleToothClick}
            selectedTeeth={selectedTeeth}
            setSelectedTeeth={setSelectedTeeth}
            service={service}
            saveEditedService={saveEditedService}
            initialTeeth={service.teeth}
            toothMinW="26px"
            toothH="28px"
            lineH="73px"
            tpStatus={tpStatus}
          />
        )} */}
      </Box>

      {/* <Reorder.Item
        value={service} // Use the entire service object or `service.id`
        id={service._id} // Unique key to ensure proper tracking
        drag={tpStatus === "planning"}
        style={{
          y,

          borderRadius: "1rem",
          background: whiteMode,
          zIndex: 1,
          position: "relative",
          marginBottom: "0.75rem",
        }}
        whileDrag={{
          background: "transparent",
          zIndex: 900,
        }}
      >
      
      </Reorder.Item> */}
    </>
  );
};

export default ServiceItemNewMobile;
